import React from 'react'

import { DataTable } from '../../../components/Table/DataTable.react'
import { Money } from '../../../components/Money/Money.react'
import Authentication from '../../../main/factory/authentication'
import countryDate from '../../../core/main/filter/country_date/filter'
import { NoChannelsBanner } from '../no_channels_banner/NoChannelsBanner.react'
import '../../i18next.react'
import { useTranslation } from 'react-i18next'

const settlementNgModule = 'merchant.application.main.factory.settlement'
const analyticsNgModule = 'core.application.main.factory.analytics'

export const SettlementsList = ({ channel, configuration, $scope, $location, [settlementNgModule]: Settlement, [analyticsNgModule]: Analytics, hasChannels = true }) => {
  const { t } = useTranslation('settlements')

  const reservesEnabled = !configuration ? false : configuration.enabled

  const ITEMS_PER_PAGE = 20

  const initialSearch = $location.search()
  const initialPage = initialSearch.page && initialSearch.page - 1

  const [data, setData] = React.useState([])
  const [isFetching, setIsFetching] = React.useState(false)
  const [totalItems, setTotalItems] = React.useState(0)
  const [currentPage, setCurrentPage] = React.useState(initialPage || 0)
  const [sortColumn, setSortColumn] = React.useState($location.search().sort || 'settlementDate')
  const [sortOrder, setSortOrder] = React.useState($location.search().direction || 'descending')

  const exportCsvUrl = ({ id, source }) => {
    return Settlement.getExportCsvUrl(Authentication.merchantId(), id, source)
  }

  const summaryExportCsvUrl = ({ id, source }) => {
    return Settlement.getSummaryExportCsvUrl(Authentication.merchantId(), id, source)
  }

  const onlineCardExportUrl = ({ uuid, reportType }) => {
    return Settlement.getOnlineCardExport(Authentication.merchantId(), uuid, reportType)
  }

  const updateLocation = ({ page, sortColumn, sortOrder }) => {
    setCurrentPage(page)
    setSortColumn(sortColumn)
    setSortOrder(sortOrder)
  }

  const isAfterpayCard = () => channel === 'IN_STORE_CARD' || channel === 'IN_STORE_CARD_ONLINE'
  const isOnlineCard = () => channel === 'ONLINE_CARD'
  const headers = () => isAfterpayCard() ? afterpayCardHeaders() : isOnlineCard() ? onlineCardHeaders() : onlineHeaders()
  const columns = () => isAfterpayCard() ? afterpayCardColumns() : isOnlineCard() ? onlineCardColumns() : onlineColumns()

  const onlineHeaders = () => {
    const columnWidth = reservesEnabled ? 15 : 20
    const headers = [
      { title: t('table-date'), width: columnWidth, sortBy: 'settlementDate' },
      { title: t('table-amount'), width: columnWidth, sortBy: 'settlementAmount' },
      { title: t('table-detail'), middle: true, width: columnWidth },
      { title: t('table-store'), middle: true, width: columnWidth }
    ]

    reservesEnabled && headers.splice(2, 0, { title: t('amount-transferred-reserves'), width: 15, sortBy: 'reservesTransferAmount' },
      { title: t('total-settled'), width: 15, sortBy: 'adjustedSettlementAmount' })

    return headers
  }

  const afterpayCardHeaders = () => [
    { title: t('table-date'), sortBy: 'settlementDate' },
    { title: t('table-amount-owned'), width: 15, sortBy: 'totalFees' },
    { title: t('table-detail'), middle: true },
    { title: t('table-store'), middle: true }
  ]

  const onlineCardHeaders = () => [
    { title: t('table-date'), sortBy: 'settlementDate' },
    { title: t('table-total-amount'), width: 15, sortBy: 'totalOrderAmount' },
    { title: t('table-detail'), middle: true },
    { title: t('table-store'), middle: true }
  ]

  const onlineColumns = () => {
    const columnWidth = reservesEnabled ? 15 : 20
    const columns = [
      {
        width: columnWidth,
        extraClassNames: 'settlement-date',
        contentFunction: ({ settlementDate }) =>
          countryDate(settlementDate, { countryCode: Authentication.countryCode() })
      }, {
        width: columnWidth,
        extraClassNames: 'total-amount',
        contentFunction: ({ settlementAmount }) =>
          <><Money {...settlementAmount} />&nbsp;<span>{settlementAmount?.currency}</span></>
      }, {
        width: columnWidth,
        middle: true,
        leftMiddle: true,
        extraClassNames: 'details',
        contentFunction: ({ id, source }) => (
          <>
            <a className='link' href={`reconciliation/${id}?source=${source}`} onClick={() => Analytics.sendAmplitudeEvent('Clicked Detail/View', { reconciliation: id, source })}>{t('view')}</a>
            &nbsp;
            <a className='link' href={exportCsvUrl({ id, source })} onClick={() => Analytics.sendAmplitudeEvent('Clicked Detail/Export', { reconciliation: id, source })}>{t('export')}</a>
          </>
        )
      }, {
        width: columnWidth,
        middle: true,
        leftMiddle: true,
        extraClassNames: 'export',
        contentFunction: ({ id, source }) => (
          <>
            <a className='link' href={`reconciliation-summary/${id}?source=${source}`} onClick={() => Analytics.sendAmplitudeEvent('Clicked BY STORE/View', { reconciliation: id, source })}>{t('view')}</a>
            &nbsp;
            <a className='link' href={summaryExportCsvUrl({ id, source })} onClick={() => Analytics.sendAmplitudeEvent('Clicked BY STORE/Export', { reconciliation: id, source })}>{t('export')}</a>
          </>
        )
      }
    ]

    reservesEnabled && columns.splice(2, 0, {
      width: 15,
      extraClassNames: 'reserve-transfer',
      contentFunction: ({ reservesTransferAmount }) =>
        <><Money {...reservesTransferAmount} />&nbsp;<span>{reservesTransferAmount?.currency}</span></>
    }, {
      width: 15,
      extraClassNames: 'adjusted-amount',
      contentFunction: ({ adjustedSettlementAmount }) =>
        <><Money {...adjustedSettlementAmount} />&nbsp;<span>{adjustedSettlementAmount?.currency}</span></>
    })

    return columns
  }

  const afterpayCardColumns = () => [
    {
      extraClassNames: 'settlement-date',
      contentFunction: ({ settlementDate }) =>
        countryDate(settlementDate, { countryCode: Authentication.countryCode() })
    }, {
      width: 15,
      extraClassNames: 'total-amount',
      contentFunction: ({ totalFees }) =>
        <><Money {...totalFees} />&nbsp;<span>{totalFees?.currency}</span></>
    }, {
      middle: true,
      leftMiddle: true,
      extraClassNames: 'details',
      contentFunction: ({ id, source }) => (
        <>
          <a className='link' href={`card-reconciliation/${id}?source=${source}`}>{t('view')}</a>
          &nbsp;
          <a className='link' href={exportCsvUrl({ id, source })}>{t('export')}</a>
        </>
      )
    }, {
      middle: true,
      leftMiddle: true,
      extraClassNames: 'export',
      contentFunction: ({ id, source }) => (
        <>
          <a className='link' href={`card-reconciliation-summary/${id}?source=${source}`}>{t('view')}</a>
          &nbsp;
          <a className='link' href={summaryExportCsvUrl({ id, source })}>{t('export')}</a>
        </>
      )
    }
  ]

  const onlineCardColumns = (merchantId) => [
    {
      extraClassNames: 'settlement-date',
      contentFunction: ({ settlementDate }) =>
        countryDate(settlementDate, { countryCode: Authentication.countryCode() })
    }, {
      width: 15,
      extraClassNames: 'total-amount',
      contentFunction: ({ totalOrderAmount }) =>
        <><Money {...totalOrderAmount} />&nbsp;<span>{totalOrderAmount?.currency}</span></>
    }, {
      middle: true,
      leftMiddle: true,
      extraClassNames: 'details',
      contentFunction: ({ uuid }) => (
        <>
          <a className='link' href={`online-card-reconciliation/${uuid}`}>{t('view')}</a>
            &nbsp;
          <a className='link' href={onlineCardExportUrl({ uuid, reportType: 'TRANSACTION_REPORT' })}>{t('export')}</a>
        </>
      )
    }, {
      middle: true,
      leftMiddle: true,
      extraClassNames: 'export',
      contentFunction: ({ uuid }) => (
        <>
          <a className='link' href={`online-card-reconciliation-summary/${uuid}`}>{t('view')}</a>
              &nbsp;
          <a className='link' href={onlineCardExportUrl({ uuid, reportType: 'INVOICE_REPORT' })}>{t('export')}</a>
        </>
      )
    }
  ]

  const reloadData = () => {
    if (isFetching) {
      return
    }

    $scope.$apply(() => {
      $location.search('page', currentPage + 1)
      $location.search('sort', sortColumn)
      $location.search('direction', sortOrder)
    })

    setIsFetching(true)

    const parameters = {
      offset: currentPage * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE,
      orderBy: sortColumn,
      ascending: sortOrder === 'ascending',
      merchantId: Authentication.merchantId(),
      channel: channel
    }

    if (channel === 'ONLINE_CARD') {
      Settlement.readOnlineCard(
        {
          limit: ITEMS_PER_PAGE,
          offset: currentPage * ITEMS_PER_PAGE,
          merchantId: Authentication.merchantId(),
          orderBy: sortColumn,
          isAscOrder: sortOrder === 'ascending'
        },
        ({ results, totalResults }) => {
          setData(results)
          setTotalItems(totalResults || 0)
          setIsFetching(false)
        },
        () => setIsFetching(false)
      )
    } else {
      Settlement.read(
        parameters,
        ({ results, totalResults }) => {
          setData(results)
          setTotalItems(totalResults || 0)
          setIsFetching(false)
        },
        () => setIsFetching(false)
      )
    }
  }

  React.useEffect(reloadData, [currentPage, sortColumn, sortOrder])

  return (
    <>
      {totalItems <= 0 &&
        !hasChannels &&
          <div className='container'>
            <div className='column-50'>
              <NoChannelsBanner />
            </div>
          </div>}
      {((totalItems >= 0 && hasChannels) || totalItems > 0) &&
        <section className='settlement-list'>
          <div className='column-100'>
            <DataTable
              headers={headers()}
              columns={columns()}
              updateLocation={updateLocation}
              data={data}
              totalItems={totalItems}
              limit={ITEMS_PER_PAGE}
              currentPage={currentPage}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
            />
          </div>
        </section>}
    </>
  )
}

export default SettlementsList
